import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  FaStar, FaShieldAlt, FaGift, FaClock, FaCheckCircle, 
  FaTimes, FaArrowRight, FaTrophy, FaGamepad, FaCreditCard
} from 'react-icons/fa';
import SEO from '../components/SEO';

interface OfferData {
  [key: string]: {
    name: string;
    image: string;
    bonus: string;
    rating: number;
    description: string;
    pros: string[];
    cons: string[];
    minDeposit: string;
    maxBonus: string;
    wagering: string;
    payoutTime: string;
    license: string;
    games: number;
    paymentMethods: string[];
  };
}

const OfferReview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState('overview');

  const offers: OfferData = {
    allbritishcasino: {
      name: 'All British Casino',
      image: '/images/offers/allbritishcasino.webp',
      bonus: '100% up to £100 + 10% Cashback',
      rating: 4.8,
      description: 'All British Casino brings a distinctly British flavor to online gambling with a comprehensive selection of games, generous bonuses, and exceptional customer service. Licensed by the UK Gambling Commission, this casino offers a secure and entertaining gaming environment.',
      pros: [
        'UK Gambling Commission licensed',
        'Fast withdrawal processing',
        'Excellent customer support',
        'Wide variety of payment methods',
        'Regular promotions and bonuses'
      ],
      cons: [
        'Limited live dealer games',
        'Some country restrictions apply'
      ],
      minDeposit: '£10',
      maxBonus: '£100',
      wagering: '35x',
      payoutTime: '24-48 hours',
      license: 'UK Gambling Commission',
      games: 500,
      paymentMethods: ['Visa', 'Mastercard', 'PayPal', 'Skrill', 'Neteller', 'Bank Transfer']
    },
    funcasino: {
      name: 'Fun Casino',
      image: '/images/offers/funcasino.webp',
      bonus: '200% up to £200 + 50 Free Spins',
      rating: 4.7,
      description: 'Fun Casino lives up to its name by delivering an entertaining and rewarding gaming experience. With a massive game library, innovative features, and a generous welcome package, this casino is perfect for both new and experienced players.',
      pros: [
        'Large game selection',
        'Attractive welcome bonus',
        'Mobile-optimized platform',
        'VIP rewards program',
        '24/7 customer support'
      ],
      cons: [
        'Higher wagering requirements',
        'Limited cryptocurrency options'
      ],
      minDeposit: '£20',
      maxBonus: '£200',
      wagering: '40x',
      payoutTime: '48-72 hours',
      license: 'UK Gambling Commission',
      games: 800,
      paymentMethods: ['Visa', 'Mastercard', 'PayPal', 'Skrill', 'Bank Transfer']
    },
    kwiff: {
      name: 'Kwiff',
      image: '/images/offers/kwiff.webp',
      bonus: '£30 Free Bet + Surprise Bets',
      rating: 4.9,
      description: 'Kwiff revolutionizes online betting with its unique "Surprise Bet" feature that randomly boosts your odds. This innovative approach, combined with a user-friendly interface and excellent mobile app, makes Kwiff stand out in the UK market.',
      pros: [
        'Unique Surprise Bet feature',
        'Excellent mobile app',
        'Quick registration process',
        'Fast payouts',
        'Innovative betting experience'
      ],
      cons: [
        'Smaller game selection',
        'Limited live streaming'
      ],
      minDeposit: '£10',
      maxBonus: '£30',
      wagering: 'N/A (Free Bet)',
      payoutTime: '24 hours',
      license: 'UK Gambling Commission',
      games: 300,
      paymentMethods: ['Visa', 'Mastercard', 'PayPal', 'Apple Pay', 'Google Pay']
    }
  };

  const offer = id ? offers[id] : null;

  if (!offer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Offer Not Found</h1>
          <Link to="/" className="text-primary-600 hover:underline">Return to Home</Link>
        </div>
      </div>
    );
  }

  // Chart data for visualization
  const chartData = {
    rating: offer.rating,
    games: offer.games,
    payoutSpeed: 85, // percentage
    bonusValue: 90, // percentage
    safety: 95 // percentage
  };

  return (
    <>
      <SEO
        title={`${offer.name} Review - Expert Analysis & Bonus Details`}
        description={`Complete review of ${offer.name}. Get ${offer.bonus} bonus, read expert analysis, compare features, and make an informed decision.`}
        type="article"
        url={`https://spinharvester.com/offers/${id}`}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Review',
          itemReviewed: {
            '@type': 'Organization',
            name: offer.name
          },
          reviewRating: {
            '@type': 'Rating',
            ratingValue: offer.rating,
            bestRating: 5
          },
          author: {
            '@type': 'Organization',
            name: 'SpinHarvester'
          }
        }}
      />

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary-50 to-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <FaStar
                      key={i}
                      className={i < Math.floor(offer.rating) ? 'text-yellow-400' : 'text-gray-300'}
                    />
                  ))}
                  <span className="text-2xl font-bold text-gray-900 ml-2">{offer.rating}/5</span>
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                  {offer.name} Review
                </h1>
                <p className="text-xl text-gray-600 mb-6">{offer.description}</p>
                <div className="bg-primary-600 text-white p-6 rounded-xl mb-6">
                  <div className="text-sm mb-2">Exclusive Bonus</div>
                  <div className="text-3xl font-bold mb-2">{offer.bonus}</div>
                  <button className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                    Claim Bonus Now
                  </button>
                </div>
              </div>
              <div className="relative">
                <img
                  src={offer.image}
                  alt={offer.name}
                  className="rounded-2xl shadow-2xl w-full"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Quick Stats */}
        <section className="bg-white py-8 border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-600 mb-1">{offer.rating}/5</div>
                <div className="text-sm text-gray-600">Rating</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-600 mb-1">{offer.games}+</div>
                <div className="text-sm text-gray-600">Games</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-600 mb-1">{offer.payoutTime}</div>
                <div className="text-sm text-gray-600">Payout</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-600 mb-1">{offer.wagering}</div>
                <div className="text-sm text-gray-600">Wagering</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-600 mb-1">✓</div>
                <div className="text-sm text-gray-600">Licensed</div>
              </div>
            </div>
          </div>
        </section>

        {/* Tabs */}
        <section className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-8 overflow-x-auto">
              {['overview', 'bonus', 'games', 'payment', 'safety'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-2 border-b-2 font-semibold whitespace-nowrap transition-colors ${
                    activeTab === tab
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Tab Content */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {activeTab === 'overview' && (
              <div className="grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-8">
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Overview</h2>
                    <p className="text-gray-700 leading-relaxed mb-6">{offer.description}</p>
                    
                    <div className="grid md:grid-cols-2 gap-6 mb-8">
                      <div className="bg-green-50 p-6 rounded-xl">
                        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <FaCheckCircle className="text-green-600" /> Pros
                        </h3>
                        <ul className="space-y-2">
                          {offer.pros.map((pro, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-gray-700">
                              <span className="text-green-600 mt-1">✓</span>
                              {pro}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-red-50 p-6 rounded-xl">
                        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <FaTimes className="text-red-600" /> Cons
                        </h3>
                        <ul className="space-y-2">
                          {offer.cons.map((con, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-gray-700">
                              <span className="text-red-600 mt-1">✗</span>
                              {con}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="bg-white p-6 rounded-xl shadow-lg border">
                    <h3 className="font-bold text-gray-900 mb-4">Key Information</h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Min Deposit:</span>
                        <span className="font-semibold">{offer.minDeposit}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Max Bonus:</span>
                        <span className="font-semibold">{offer.maxBonus}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Wagering:</span>
                        <span className="font-semibold">{offer.wagering}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">License:</span>
                        <span className="font-semibold">{offer.license}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Chart Visualization */}
                  <div className="bg-white p-6 rounded-xl shadow-lg border">
                    <h3 className="font-bold text-gray-900 mb-6">Performance Metrics</h3>
                    <div className="space-y-4">
                      {[
                        { label: 'Rating', value: chartData.rating * 20, max: 100 },
                        { label: 'Game Variety', value: (chartData.games / 10), max: 100 },
                        { label: 'Payout Speed', value: chartData.payoutSpeed, max: 100 },
                        { label: 'Bonus Value', value: chartData.bonusValue, max: 100 },
                        { label: 'Safety', value: chartData.safety, max: 100 }
                      ].map((metric, idx) => (
                        <div key={idx}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">{metric.label}</span>
                            <span className="font-semibold">{Math.round(metric.value)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-primary-600 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${Math.min(metric.value, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'bonus' && (
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Bonus Details</h2>
                <div className="bg-primary-50 p-8 rounded-xl mb-6">
                  <div className="text-4xl font-bold text-primary-600 mb-2">{offer.bonus}</div>
                  <p className="text-gray-700 mb-4">Welcome Bonus Package</p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <FaCheckCircle className="text-green-600" />
                      <span>Minimum deposit: {offer.minDeposit}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FaCheckCircle className="text-green-600" />
                      <span>Maximum bonus: {offer.maxBonus}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FaCheckCircle className="text-green-600" />
                      <span>Wagering requirement: {offer.wagering}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'games' && (
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Game Selection</h2>
                <div className="bg-white p-8 rounded-xl shadow-lg">
                  <div className="text-4xl font-bold text-primary-600 mb-2">{offer.games}+</div>
                  <p className="text-gray-700 mb-6">Games Available</p>
                  <div className="grid md:grid-cols-3 gap-4">
                    {['Slots', 'Table Games', 'Live Casino', 'Jackpots', 'Video Poker', 'Specialty Games'].map((game, idx) => (
                      <div key={idx} className="bg-gray-50 p-4 rounded-lg text-center">
                        <FaGamepad className="text-2xl text-primary-600 mx-auto mb-2" />
                        <div className="font-semibold">{game}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'payment' && (
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Payment Methods</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {offer.paymentMethods.map((method, idx) => (
                    <div key={idx} className="bg-white p-6 rounded-xl shadow-lg text-center">
                      <FaCreditCard className="text-3xl text-primary-600 mx-auto mb-3" />
                      <div className="font-semibold text-gray-900">{method}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 bg-gray-50 p-6 rounded-xl">
                  <h3 className="font-bold text-gray-900 mb-3">Withdrawal Information</h3>
                  <p className="text-gray-700">Average payout time: <strong>{offer.payoutTime}</strong></p>
                  <p className="text-gray-700">Minimum withdrawal: <strong>£10</strong></p>
                </div>
              </div>
            )}

            {activeTab === 'safety' && (
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Safety & Security</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-lg">
                    <FaShieldAlt className="text-4xl text-primary-600 mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Licensing</h3>
                    <p className="text-gray-700">{offer.license}</p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-lg">
                    <FaShieldAlt className="text-4xl text-primary-600 mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Security</h3>
                    <p className="text-gray-700">SSL encryption and secure payment processing</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default OfferReview;


